import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { LabeledStat } from '@/components/common/StatCardGrid';
import { dailyDashboardQueryOptions } from '../api/attendance.api';

export function useDailyDashboard(
  date: string,
  search: string,
  filters: Record<string, string>,
  isActive?: boolean,
) {
  const {
    data = [],
    isLoading,
    error,
    refetch,
  } = useQuery(dailyDashboardQueryOptions({ date, isActive }));

  const filtered = useMemo(() => {
    const q = search.trim().replace(/\s+/gu, ' ').toLowerCase();

    return data.filter((item) => {
      if (q) {
        const haystack = [
          item.zmName,
          item.amName,
          item.zmUsername,
          item.zmMobile,
          item.thana,
          item.district,
        ];
        if (!haystack.some((v) => v?.toLowerCase().includes(q) ?? false)) return false;
      }
      if (filters.status && item.status !== filters.status) return false;
      if (
        filters.division &&
        !item.division?.toLowerCase().includes(filters.division.toLowerCase())
      )
        return false;
      if (
        filters.district &&
        !item.district?.toLowerCase().includes(filters.district.toLowerCase())
      )
        return false;
      return true;
    });
  }, [data, search, filters]);

  const summary = useMemo((): LabeledStat[] => {
    const counts = { P: 0, L: 0, A: 0, W: 0, H: 0 };
    filtered.forEach((item) => {
      counts[item.status]++;
    });
    return [
      { key: 'total', label: 'Total', value: filtered.length },
      { key: 'present', label: 'Present', value: counts.P },
      { key: 'late', label: 'Late', value: counts.L },
      { key: 'absent', label: 'Absent', value: counts.A },
      { key: 'holiday', label: 'Holiday', value: counts.H },
    ];
  }, [filtered]);

  return { filtered, summary, isLoading, error, refetch };
}
